import { Flex } from '@/ui/Flex/Flex';
import { Input } from '@/ui/Input/Input';
import { T } from '@/ui/Text/Text';
import { FormattedMessage, useIntl } from 'react-intl';
import { IconCustomEventSecondary } from '@/icons';
import HorizontalDivider from '@/ui/HorizontalDivider';
import { Textarea } from '@/ui/Textarea/Textarea';
import { css } from 'styled-components';
import { ActionDispatchCustomEventNodeType } from '../../Nodes/types';
import FAQSection from '../FAQSection';
import { StyledSettings, StyledSettingsContent } from '../styles';
import { GeneralSettingsType } from '../types';

function DispatchCustomEventSettings({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  settings,
  selectedNode,
  updateNodeData,
  disabled,
}: {
  settings: GeneralSettingsType;
  selectedNode: ActionDispatchCustomEventNodeType;
  updateNodeData: (id: string, newData: any) => void;
  disabled?: boolean;
}) {
  const intl = useIntl();

  const relevantData = selectedNode.data.options;
  const faqs = [
    {
      question: intl.formatMessage({
        defaultMessage: 'What is a custom event?',
        id: 'Hyrh7N',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'A custom event is a user-defined event that can be dispatched. It uses an API-Call to communicate with the local REST API. You can then listen to this event to trigger custom interactions.',
        id: 'ElMRrG',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'What is the Event Data?',
        id: 'DN7wKF',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'The Event Data is a JSON object that can contain any data you want to pass along with the event. It has to be a valid JSON format.',
        id: 'I4dSig',
      }),
    },
  ];

  return (
    <StyledSettings data-testid="action-dispatchCustomEvent-settings">
      <Flex align="center" gap={8}>
        <IconCustomEventSecondary />
        <Flex direction="column">
          <T h3>
            {intl.formatMessage({
              defaultMessage: 'Dispatch Custom Event',
              id: 'sOUA1M',
            })}
          </T>
        </Flex>
      </Flex>
      <StyledSettingsContent>
        <Flex direction="column" gap={8}>
          <Flex direction="column" gap={4}>
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Event Name',
                id: 'n5Q9H0',
              })}
            </T>
            <Input
              disabled={disabled ?? false}
              required
              errorPosition="bottom"
              size="small"
              value={relevantData.eventName ?? ''}
              onChange={(e) =>
                updateNodeData(selectedNode.id, {
                  ...selectedNode.data,
                  options: { ...relevantData, eventName: e },
                })
              }
              placeholder={intl.formatMessage({
                defaultMessage: 'Event Name',
                id: 'n5Q9H0',
              })}
            />
          </Flex>
        </Flex>
        <Flex direction="column" gap={8}>
          <Flex direction="column" gap={4}>
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Event Data',
                id: 'n4rQL6',
              })}
            </T>
            <div style={{ position: 'relative' }}>
              <Textarea
                disabled={disabled ?? false}
                value={relevantData.eventData ?? ''}
                onChange={(e) =>
                  updateNodeData(selectedNode.id, {
                    ...selectedNode.data,
                    options: {
                      ...relevantData,
                      eventData: e.target.value,
                    },
                  })
                }
                placeholder={intl.formatMessage({
                  defaultMessage: 'Add JSON',
                  id: 'q5DLJS',
                })}
                styles={css`
                  min-height: 150px;
                  max-height: 400px;
                  overflow: auto;
                  resize: vertical;
                `}
                required
              />
              {(() => {
                try {
                  if (
                    relevantData.eventData &&
                    relevantData.eventData.trim() !== ''
                  ) {
                    JSON.parse(relevantData.eventData);
                  }
                  return null;
                } catch {
                  return (
                    <div
                      style={{
                        color: '#dd6c6c',
                        fontSize: '12px',
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                      }}
                    >
                      <FormattedMessage
                        defaultMessage="Invalid JSON format"
                        id="YLE013"
                      />
                    </div>
                  );
                }
              })()}
            </div>
          </Flex>
        </Flex>

        <HorizontalDivider />
      </StyledSettingsContent>
      <FAQSection faqs={faqs} />
    </StyledSettings>
  );
}
export default DispatchCustomEventSettings;
